
// utils/date.ts

export const toLocalDateString = (date: Date): string => {
    if (!date || isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const parseLocalDate = (dateStr: string): Date => {
    if (!dateStr) return new Date();
    if (dateStr.includes('T')) {
        return new Date(dateStr);
    }
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        const [y, m, d] = parts.map(Number);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m - 1, d);
        }
    }
    return new Date(dateStr);
};

export const getWeekInfo = (dateStr: string) => {
    if (!dateStr) {
        return { key: 'invalid-date', label: 'Ungültiges Datum', startDate: new Date(0) };
    }
    const date = parseLocalDate(dateStr);
    if (isNaN(date.getTime())) {
        return { key: 'invalid-date', label: 'Ungültiges Datum', startDate: new Date(0) };
    }
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    const year = d.getUTCFullYear();
    
    const firstDayOfWeek = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayOfWeek = firstDayOfWeek.getDay(); 
    const diffToMonday = firstDayOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(firstDayOfWeek.getFullYear(), firstDayOfWeek.getMonth(), diffToMonday);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    const formatDate = (d: Date) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

    return {
        key: `${year}-KW${String(weekNo).padStart(2, '0')}`,
        label: `Kalenderwoche ${weekNo} (${formatDate(monday)} - ${formatDate(sunday)})`,
        startDate: monday,
    };
};

export const getWeekStartDateFromKey = (weekKey: string): Date => {
    if (!weekKey || !weekKey.includes('-KW')) return new Date();
    const [yearStr, weekStr] = weekKey.split('-KW');
    const year = parseInt(yearStr);
    const weekNumber = parseInt(weekStr);
    if (isNaN(year) || isNaN(weekNumber)) return new Date();

    const firstDayOfYear = new Date(year, 0, 1);
    const daysOffset = (weekNumber - 1) * 7;
    const dateInWeek = new Date(firstDayOfYear.getTime() + daysOffset * 86400000);
    const dayOfWeek = dateInWeek.getDay();
    const diffToMonday = dateInWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(dateInWeek.setDate(diffToMonday));
    monday.setHours(0, 0, 0, 0);
    return monday;
};

export const getWeekDateRange = (weekKey: string): string => {
    if (weekKey === 'invalid-date' || !weekKey.includes('-KW')) return 'Ungültiges Datum';
    const [yearStr, weekStr] = weekKey.split('-KW');
    const year = parseInt(yearStr);
    const weekNumber = parseInt(weekStr);

    if (isNaN(year) || isNaN(weekNumber)) return 'Ungültiges Datum';

    const firstDayOfYear = new Date(year, 0, 1);
    const daysOffset = (weekNumber - 1) * 7;
    
    const dateInWeek = new Date(firstDayOfYear.getTime() + daysOffset * 86400000);
    
    const dayOfWeek = dateInWeek.getDay();
    const diffToMonday = dateInWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(dateInWeek.setDate(diffToMonday));

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const formatDate = (d: Date) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

    return `${formatDate(monday)} - ${formatDate(sunday)}`;
};

export const calculateDuration = (entryDateStr: string): string => {
    if (!entryDateStr || isNaN(new Date(entryDateStr).getTime())) {
        return 'N/A';
    }
    const entryDate = new Date(entryDateStr);
    const now = new Date();

    if (entryDate > now) return 'Zukünftiger Eintritt';
    
    const diffTime = now.getTime() - entryDate.getTime();
    if (diffTime < 1000 * 60 * 60 * 24) return "Heute";

    let years = now.getFullYear() - entryDate.getFullYear();
    let months = now.getMonth() - entryDate.getMonth();
    let days = now.getDate() - entryDate.getDate();

    if (days < 0) {
        months--;
        const lastDayOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        days += lastDayOfPreviousMonth;
    }
    if (months < 0) {
        years--;
        months += 12;
    }
    
    const totalMonths = years * 12 + months;
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;

    const parts: string[] = [];
    if (totalMonths > 0) parts.push(`${totalMonths} Monat${totalMonths > 1 ? 'en' : ''}`);
    if (weeks > 0) parts.push(`${weeks} Woche${weeks > 1 ? 'n' : ''}`);
    if (remainingDays > 0) parts.push(`${remainingDays} Tag${remainingDays > 1 ? 'en' : ''}`);
    
    if(parts.length === 0) {
        const totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        if (totalDays > 0) return `${totalDays} Tag${totalDays > 1 ? 'en' : ''}`;
    }
    
    return parts.join(', ');
};

export const formatLastActive = (dateStr?: string): string => {
    if (!dateStr || isNaN(new Date(dateStr).getTime())) return 'nie';
    
    const date = new Date(dateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'gerade eben';
    if (diffInSeconds < 3600) return `vor ${Math.floor(diffInSeconds / 60)} Min.`;
    if (diffInSeconds < 86400) return `vor ${Math.floor(diffInSeconds / 3600)} Std.`;
    
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export const formatDateTime = (dateStr?: string): string => {
    if (!dateStr || isNaN(new Date(dateStr).getTime())) return 'nie';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
    });
};

/**
 * Ermittelt den spätesten Zeitstempel aus einer Liste von potenziellen Aktivitätsdaten.
 */
export const getSmartLastActive = (dates: (string | undefined | null)[]): string | undefined => {
    const validDates = dates
        .filter((d): d is string => !!d && !isNaN(new Date(d).getTime()))
        .map(d => new Date(d).getTime());
    
    if (validDates.length === 0) return undefined;
    
    const latestTimestamp = Math.max(...validDates);
    return new Date(latestTimestamp).toISOString();
};
